/**
 * User identity helpers.
 *
 * Ported from TechTarget's legacy context.user.js:
 * bitbucket/uxwebsite/vgn-responsive/src/main/js/context.user.js
 * (see activityTracking.js for background on why/how this code is being ported)
 *
 * Split out of activityTracking.js so user-identity logic has a home that isn't tied to
 * activity tracking specifically.
 */
import Cookies from 'js-cookie';
import TempSubscriberPassHandler from './editorial_gate/temp_subscriber_pass_handler';

/*
 * Ported from context.user.js's getUser(). Resolves a known user id from cookies/params,
 * cheapest/most-authoritative first. Divesite doens't have tt member signup flow
 * yet, and we're not sure if cookies will be carried over, so this might not do anything.
 */
const getTTuser = () => {
  const user = {};
  const crs = Cookies.get('crs');
  if (crs) {
    // TODO: divesite has no `crs` cookie issuer yet (no registered-reader login system).
    // Once one exists, confirm its JSON shape matches what's assumed here.
    try {
      Object.assign(user, JSON.parse(crs));
    } catch (e) {
      // malformed crs cookie; ignore and fall through with an empty user
    }
    user.loggedIn = true;
  } else if (Cookies.get('tt_ut')) {
    user.uid = Cookies.get('tt_ut');
  } else if (Cookies.get('uidLoggedIn')) {
    const result = /\bdgUserId(\d+)\b/.exec(Cookies.get('uidLoggedIn'));
    if (result) [, user.uid] = result;
  }
  // The original also trusted a ?uid= URL param; dropped since anyone can add it to a link.
  // NOTE: the original also had a `cdnuli` ("CDN User Logged Status") branch that
  // hardcoded user.uid = 1 when no crs cookie was present. That looked like dead/legacy
  // logic specific to TT's CDN setup rather than a real identity source, so it wasn't
  // ported here.
  //
  // TODO: the original also wrote a tt_ut cookie (5yr expiry) the first time a uid became
  // known this way, dropping any old ad_ut cookie, so later visits could skip straight to
  // the tt_ut branch above. Not ported: there's no crs issuer yet to base that write on.
  return user;
};

const isTTMember = () => {
  const ttUser = getTTuser();
  return Boolean(ttUser.loggedIn || ttUser.uid);
};

const isRecentSignup = () => {
  // We make a push to localstorage on user signup. This checks for the presence of that string
  // Only valid for 24 hours, by which time lytics segments should be correctly updated
  const handler = new TempSubscriberPassHandler();
  return Boolean(handler.hasValidToken());
};

const hasSailthruCookie = () => {
  return Boolean(Cookies.get('sailthru_hid'));
};

// If Lytics' entityReady callback never fires (blocked script, slow network, etc.) or hands
// back a profile shape we don't expect, don't leave callers awaiting this forever.
const LYTICS_ENTITY_READY_TIMEOUT_MS = 3000;

const isLyticsSubscriber = () => new Promise((resolve) => {
  if (typeof window.jstag === 'undefined') {
    resolve(false);
    return;
  }

  let settled = false;
  const settle = (value) => {
    if (settled) return;
    settled = true;
    resolve(value);
  };

  setTimeout(() => settle(false), LYTICS_ENTITY_READY_TIMEOUT_MS);

  // This is pulled from lyticsCallback in editorial_gate.js
  window.jstag.call('entityReady', (profile) => {
    try {
      // All sub lists follow the any_DIVE-NAME_subs format.
      // For example, Retail Dive is any_retail_dive_subs.
      // All trial lists follow the all_DIVE-NAME_trial_subscribers format.
      const usersAudienceArray = profile.data.user.segments;
      // we expect lytics segments to be in the format of `any_DIVE-NAME_subs`
      // so that we can check if the user is a subscriber or not.
      // where DIVE-NAME is lowercase, spaces replaced with underscores,
      // and dashes removed.
      // Examples
      // Retail Dive -> any_retail_dive_subs,
      // C-Store Dive-> any_cstore_dive_subs

      // TODO: THIS WILL NEED TO BE UPDATED WITH COMMUNITIES ON TECHTARGET
      const diveName = window.siteName.toLowerCase()
        .replace(/ /g, '_')
        .replace(/-/g, '');
      const subList = `any_${diveName}_subs`;
      const trialList = `all_${diveName}_trial_subscribers`;
      const isSub = usersAudienceArray.indexOf(subList) > -1;
      const isTrial = usersAudienceArray.indexOf(trialList) > -1;

      settle(Boolean(isSub || isTrial));
    } catch (e) {
      // Unexpected profile shape (e.g. no segments assigned yet) - treat as not-a-subscriber
      // rather than throwing inside an async callback the surrounding Promise can't catch.
      settle(false);
    }
  });
});

const isRecognizedUser = async () => {
  if (
    isTTMember()
    || isRecentSignup()
    || hasSailthruCookie()
  ) {
    return true;
  }

  return isLyticsSubscriber();
};

export {
  getTTuser,
  isTTMember,
  isRecentSignup,
  hasSailthruCookie,
  isLyticsSubscriber,
  isRecognizedUser,
};
